import React from 'react';
import { Alert, Button, Card, Container, Row, Col } from 'react-bootstrap';
import toast from 'react-hot-toast';

// Error Boundary Class Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console and external service
    
    this.setState({
      error,
      errorInfo,
      hasError: true
    });

    // Report to error tracking service (if available)
    if (window.errorTracker) {
      window.errorTracker.captureException(error, {
        extra: errorInfo,
        tags: {
          component: this.props.component || 'Unknown',
          boundary: 'ErrorBoundary'
        }
      });
    }

    // Show toast notification
    toast.error('An unexpected error occurred. Please try refreshing the page.');
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom error UI
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error,
          this.state.errorInfo,
          this.handleRetry
        );
      }

      // Default error UI
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
          retryCount={this.state.retryCount}
          maxRetries={this.props.maxRetries || 3}
          showDetails={this.props.showDetails || false}
        />
      );
    }

    return this.props.children;
  }
}

// Error Fallback Component
const ErrorFallback = ({
  error,
  errorInfo,
  onRetry,
  onReload,
  retryCount = 0,
  maxRetries = 3,
  showDetails = false
}) => {
  const [showDetailsState, setShowDetailsState] = React.useState(showDetails);

  const getErrorMessage = (error) => {
    if (!error) return 'An unexpected error occurred';
    
    // Network errors
    if (error.name === 'NetworkError' || error.message.includes('fetch')) {
      return 'Network connection error. Please check your internet connection and try again.';
    }
    
    // Permission errors
    if (error.message.includes('403') || error.message.includes('Forbidden')) {
      return 'You do not have permission to access this resource. Please contact your administrator.';
    }
    
    // Authentication errors
    if (error.message.includes('401') || error.message.includes('Unauthorized')) {
      return 'Your session has expired. Please log in again.';
    }
    
    // Server errors
    if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
      return 'Server error. Please try again later or contact support if the problem persists.';
    }
    
    // Generic error
    return error.message || 'An unexpected error occurred';
  };

  const getErrorIcon = (error) => {
    if (!error) return '⚠️';
    
    if (error.name === 'NetworkError' || error.message.includes('fetch')) {
      return '🌐';
    }
    
    if (error.message.includes('403') || error.message.includes('401')) {
      return '🔒';
    }
    
    if (error.message.includes('500')) {
      return '🔧';
    }
    
    return '⚠️';
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="text-center shadow-sm">
            <Card.Body className="p-4">
              <div className="mb-4">
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                  {getErrorIcon(error)}
                </div>
                <h3 className="text-danger mb-3">Oops! Something went wrong</h3>
                <p className="text-muted mb-4">
                  {getErrorMessage(error)}
                </p>
              </div>

              <div className="d-flex gap-2 justify-content-center mb-4">
                {retryCount < maxRetries && (
                  <Button
                    variant="primary"
                    onClick={onRetry}
                    disabled={retryCount >= maxRetries}
                  >
                    Try Again {retryCount > 0 && `(${retryCount}/${maxRetries})`}
                  </Button>
                )}
                
                <Button
                  variant="outline-secondary"
                  onClick={onReload}
                >
                  Refresh Page
                </Button>
              </div>

              {error && (
                <div className="text-start">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setShowDetailsState(!showDetailsState)}
                    className="p-0 text-decoration-none"
                  >
                    {showDetailsState ? '▼' : '▶'} Technical Details
                  </Button>
                  
                  {showDetailsState && (
                    <Alert variant="light" className="mt-3 text-start">
                      <small>
                        <strong>Error:</strong> {error.toString()}
                        <br />
                        <strong>Stack:</strong>
                        <pre className="mt-2 small text-muted">
                          {error.stack}
                        </pre>
                        {errorInfo && (
                          <>
                            <strong>Component Stack:</strong>
                            <pre className="mt-2 small text-muted">
                              {errorInfo.componentStack}
                            </pre>
                          </>
                        )}
                      </small>
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

// Async Error Boundary Hook
export const useAsyncError = () => {
  const [, setError] = React.useState();
  
  return React.useCallback(
    (error) => {
      setError(() => {
        throw error;
      });
    },
    [setError]
  );
};

// Error Alert Component
export const ErrorAlert = ({
  error,
  onRetry,
  onDismiss,
  showRetry = true,
  variant = 'danger',
  className = ''
}) => {
  if (!error) return null;

  const errorMessage = typeof error === 'string' ? error : error.message || 'An error occurred';

  return (
    <Alert variant={variant} className={className} dismissible={!!onDismiss} onClose={onDismiss}>
      <Alert.Heading>
        <i className="bi bi-exclamation-triangle me-2"></i>
        Error
      </Alert.Heading>
      <p className="mb-0">{errorMessage}</p>
      {showRetry && onRetry && (
        <>
          <hr />
          <div className="d-flex justify-content-end">
            <Button variant={`outline-${variant}`} size="sm" onClick={onRetry}>
              Try Again
            </Button>
          </div>
        </>
      )}
    </Alert>
  );
};

// Network Error Component
export const NetworkError = ({ onRetry, className = '' }) => {
  return (
    <div className={`text-center py-5 ${className}`}>
      <div className="mb-4">
        <i className="bi bi-wifi-off" style={{ fontSize: '4rem', color: '#dc3545' }}></i>
      </div>
      <h4 className="text-danger mb-3">Connection Problem</h4>
      <p className="text-muted mb-4">
        Unable to connect to the server. Please check your internet connection.
      </p>
      {onRetry && (
        <Button variant="primary" onClick={onRetry}>
          <i className="bi bi-arrow-clockwise me-2"></i>
          Try Again
        </Button>
      )}
    </div>
  );
};

// Permission Error Component
export const PermissionError = ({ message, onLogin, className = '' }) => {
  return (
    <div className={`text-center py-5 ${className}`}>
      <div className="mb-4">
        <i className="bi bi-lock" style={{ fontSize: '4rem', color: '#ffc107' }}></i>
      </div>
      <h4 className="text-warning mb-3">Access Denied</h4>
      <p className="text-muted mb-4">
        {message || 'You do not have permission to access this resource.'}
      </p>
      {onLogin && (
        <Button variant="warning" onClick={onLogin}>
          <i className="bi bi-person-circle me-2"></i>
          Log In
        </Button>
      )}
    </div>
  );
};

// Not Found Error Component
export const NotFoundError = ({ message, onGoBack, className = '' }) => {
  return (
    <div className={`text-center py-5 ${className}`}>
      <div className="mb-4">
        <i className="bi bi-question-circle" style={{ fontSize: '4rem', color: '#6c757d' }}></i>
      </div>
      <h4 className="text-muted mb-3">Not Found</h4>
      <p className="text-muted mb-4">
        {message || 'The requested resource could not be found.'}
      </p>
      {onGoBack && (
        <Button variant="secondary" onClick={onGoBack}>
          <i className="bi bi-arrow-left me-2"></i>
          Go Back
        </Button>
      )}
    </div>
  );
};

// Server Error Component
export const ServerError = ({ onRetry, onReload, className = '' }) => {
  return (
    <div className={`text-center py-5 ${className}`}>
      <div className="mb-4">
        <i className="bi bi-server" style={{ fontSize: '4rem', color: '#dc3545' }}></i>
      </div>
      <h4 className="text-danger mb-3">Server Error</h4>
      <p className="text-muted mb-4">
        Something went wrong on our end. Please try again later.
      </p>
      <div className="d-flex gap-2 justify-content-center">
        {onRetry && (
          <Button variant="primary" onClick={onRetry}>
            <i className="bi bi-arrow-clockwise me-2"></i>
            Try Again
          </Button>
        )}
        {onReload && (
          <Button variant="outline-secondary" onClick={onReload}>
            <i className="bi bi-arrow-clockwise me-2"></i>
            Refresh Page
          </Button>
        )}
      </div>
    </div>
  );
};

// Error Handler Hook
export const useErrorHandler = () => {
  const throwError = useAsyncError();
  
  return React.useCallback((error, context = '') => {
    
    // Log to external service if available
    if (window.errorTracker) {
      window.errorTracker.captureException(error, {
        extra: { context },
        tags: { source: 'useErrorHandler' }
      });
    }
    
    // Determine error type and show appropriate message
    if (error.name === 'NetworkError' || error.message.includes('fetch')) {
      toast.error('Network connection error. Please check your internet connection.');
    } else if (error.message.includes('401')) {
      toast.error('Session expired. Please log in again.');
      // Redirect to login page
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } else if (error.message.includes('403')) {
      toast.error('You do not have permission to perform this action.');
    } else if (error.message.includes('500')) {
      toast.error('Server error. Please try again later.');
    } else {
      toast.error(error.message || 'An unexpected error occurred.');
    }
    
    // Throw error to be caught by Error Boundary
    throwError(error);
  }, [throwError]);
};

// HOC for wrapping components with error boundary
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  return function WrappedComponent(props) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
};

export default ErrorBoundary; 